import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

async function getRecentlyViewedMarkups(sectionId) {
  const productIds = RecentlyViewed.getProducts();
  if (productIds.length === 0) return [];

  const renderedSectionId = sectionId === 'predictive-search' ? sectionId : 'main';
  return Promise.all(
    productIds.map((id) => {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', `id:${id}`);
      url.searchParams.set('resources[type]', 'product');
      return sectionRenderer.getSectionHTML(renderedSectionId, false, url);
    })
  );
}

async function getRelatedProductsMarkup(sectionId, productId, limit, recommendationsUrl) {
  if (!productId || !recommendationsUrl) return null;

  const url = new URL(recommendationsUrl, location.origin);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', limit || '4');
  url.searchParams.set('intent', 'related');

  return sectionRenderer.getSectionHTML(sectionId, false, url);
}

class RecentlyViewedRail extends HTMLElement {
  async connectedCallback() {
    try {
      const productList = this.querySelector('[data-recently-viewed-products]');
      const markups = await getRecentlyViewedMarkups('predictive-search');
      if (!markups.length || !productList) return;

      for (const markup of markups) {
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        for (const card of documentFragment.querySelectorAll('.predictive-search-results__card--product')) {
          const sourceLink = card.querySelector('.resource-card a[href]');
          const sourceImage = card.querySelector('.resource-card img');
          if (!sourceLink || !sourceImage) continue;

          const link = document.createElement('a');
          link.href = sourceLink.href;
          link.className = 'recently-viewed-rail__product';
          link.setAttribute('aria-label', sourceImage.alt || sourceLink.textContent.trim());
          link.append(sourceImage.cloneNode());
          productList.append(link);
        }
      }

      this.hidden = productList?.childElementCount === 0;
    } catch (error) {
      console.warn('Recently viewed products could not be loaded.', error);
    }
  }
}

class TvaraProductTabs extends HTMLElement {
  connectedCallback() {
    const tabItems = [...this.querySelectorAll('.tvara-product-tabs__tab-item')];
    const tabList = this.querySelector('.tvara-product-tabs__tabs');
    if (tabItems.length && tabList) {
      const nav = document.createElement('div');
      nav.className = 'tvara-product-tabs__nav';
      nav.setAttribute('role', 'tablist');
      const panels = document.createElement('div');
      panels.className = 'tvara-product-tabs__panels';
      tabItems.forEach((item) => {
        const tab = item.querySelector('[role="tab"]');
        const panel = item.querySelector('[role="tabpanel"]');
        if (tab) nav.append(tab);
        if (panel) panels.append(panel);
        item.remove();
      });
      tabList.replaceChildren(nav, panels);
    }

    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    if (this.tabs.length) this.select(this.tabs[0]);
    this.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const current = this.tabs.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      const offset = event.key === 'ArrowRight' ? 1 : -1;
      this.tabs[(current + offset + this.tabs.length) % this.tabs.length].focus();
    });
    this.loadRelatedProducts();
    this.loadRecentlyViewed();
  }

  select(selected) {
    this.tabs.forEach((tab) => {
      const active = tab === selected;
      tab.setAttribute('aria-selected', active);
      tab.tabIndex = active ? 0 : -1;
      this.querySelector(`#${tab.getAttribute('aria-controls')}`).hidden = !active;
    });
  }

  async loadRelatedProducts() {
    const grid = this.querySelector('[data-related-products-grid]');
    if (!grid || grid.dataset.productsSource !== 'related' || !this.dataset.sectionId) return;

    try {
      const markup = await getRelatedProductsMarkup(
        this.dataset.sectionId,
        this.dataset.productId,
        this.dataset.productsToShow,
        this.dataset.recommendationsUrl
      );
      if (!markup) return;
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const renderedGrid = documentFragment.querySelector('[data-related-products-grid]');
      if (!renderedGrid || renderedGrid.childElementCount === 0) return;

      grid.replaceChildren(...renderedGrid.children);
    } catch (error) {
      console.warn('Related products could not be loaded.', error);
    }
  }

  async loadRecentlyViewed() {
    const grid = this.querySelector('[data-recently-viewed-grid]');
    const empty = this.querySelector('[data-recently-viewed-empty]');
    if (!grid || !empty || !this.dataset.sectionId) return;

    try {
      const markups = await getRecentlyViewedMarkups(this.dataset.sectionId);
      if (!markups.length) return;
      const cardsById = new Map();
      markups.forEach((markup) => {
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        documentFragment.querySelectorAll('.product-grid__item[data-product-id]').forEach((item) => {
          cardsById.set(item.dataset.productId, item.querySelector('product-card'));
        });
      });
      const cards = RecentlyViewed.getProducts()
        .map((id) => cardsById.get(String(id)))
        .filter(Boolean)
        .slice(0, Number(this.dataset.productsToShow) || 4)
        .map((card) => {
          const item = document.createElement('div');
          item.className = 'resource-list__item';
          item.append(card.cloneNode(true));
          return item;
        });
      if (cards.length === 0) return;

      grid.replaceChildren(...cards);
      grid.hidden = false;
      empty.hidden = true;
    } catch (error) {
      console.warn('Recently viewed products could not be loaded.', error);
    }
  }
}

if (!customElements.get('recently-viewed-rail')) {
  customElements.define('recently-viewed-rail', RecentlyViewedRail);
}

if (!customElements.get('tvara-product-tabs')) {
  customElements.define('tvara-product-tabs', TvaraProductTabs);
}
